import { EventEmitter, inject, Injectable, resource, ResourceRef, Signal, signal } from '@angular/core';

import { applyEach, ChildFieldContext, disabled, email, form, pattern, required, SchemaPathTree, TreeValidationResult, validateAsync } from '@angular/forms/signals';
import { lastValueFrom, of, switchMap, take, timer } from 'rxjs';
import { AccountEditInterface } from '../interfaces/vrc/account-edit.interface';
import { AccountInterface } from '../interfaces/vrc/account.interface';
import { InterestInterface } from '../interfaces/vrc/account/interest.interface';
import { LoyalityDetailsInterface } from '../interfaces/vrc/account/loyality-details.interface';
import { EmailValidationInterface } from '../interfaces/vrc/email-validation.interface';
import { PhoneValidationInterface } from '../interfaces/vrc/phone-validation.interface';
import { SendOfferInterface } from '../interfaces/vrc/send-offer.interface';
import { MergeSource } from '../models/merge-source.enum';
import { PaymentTransactionType } from '../models/payment-transaction-type.enum';
import { VrcConfigService } from './vrc-config.service';
import { VrcDataService } from './vrc-data.service';

@Injectable()
export class VrcStorageService {
  private _srvc = inject(VrcDataService);
  private _config = inject(VrcConfigService);
  private _phonesSchema(p: SchemaPathTree<{ id: number, data: string }>) {
    required(p.data, { message: 'Phone is required' });
    pattern(p.data, /^\+[1-9]\d{7,17}$/, { message: 'Phone is invalid' });
  }
  private _emailSchema(e: SchemaPathTree<{ id: number, data: string }>) {
    required(e.data, { message: 'Email is required' });
    email(e.data, { message: 'Email is invalid' });
  }
  private lastEmailCall = "";
  private lastPhoneCall = "";
  public returnRoute: string | null = "";
  public editModel = signal<AccountEditInterface>({} as AccountEditInterface);
  public formModel = form(this.editModel, (s) => {
    disabled(s, { when: () => !this.isEditOn() });
    required(s.gender, { message: 'Gender is required', when: () => this.isCreateLoyalty() });
    required(s.firstName, { message: 'First name is required', when: () => this.isCreateLoyalty() });
    pattern(s.firstName, /^[^?<>!&%&#+;]*$/, { message: 'First name is invalid' });
    required(s.lastName, { message: 'Last name is required', when: () => this.isCreateLoyalty() });
    pattern(s.lastName, /^[^?<>!&%&#+;]*$/, { message: 'Last name is invalid' });
    required(s.language, { message: 'Language is required' });
    required(s.address.country, { message: 'Country is required', when: () => this.isCreateLoyalty() });
    required(s.contact.email, { message: 'Email is required', when: () => this.isCreateLoyalty() });
    required(s.contact.email, { message: 'Email or Phone is required', when: ({ valueOf }) => !valueOf(s.contact?.phone) && !this.isCreateLoyalty() });
    email(s.contact.email, { message: 'Email is invalid' });
    required(s.contact.phone, { message: 'Phone is required', when: () => this.isCreateLoyalty() });
    required(s.contact.phone, { message: 'Phone or Email is required', when: ({ valueOf }) => !valueOf(s.contact?.email) && !this.isCreateLoyalty() });
    pattern(s.contact.phone, /^\+[1-9]\d{7,17}$/, { message: 'Phone is invalid' });
    validateAsync(s.contact.email, {
      params: (ctx: ChildFieldContext<string>): { email: string, ctx: ChildFieldContext<string> } => {
        this.lastEmailCall = ctx.value();
        return ({ email: ctx.value(), ctx: ctx });
      },
      factory: (p: Signal<{ email: string, ctx: ChildFieldContext<string> } | undefined>): ResourceRef<EmailValidationInterface | undefined> => resource({
        params: () => {
          return p();
        },
        loader: (p) => {
          return lastValueFrom((timer(1000)).pipe(switchMap(() => {
            if (this.lastEmailCall !== p.params.email || p.params.ctx.state.errors().filter(x => x.kind !== "remoteValidateEmail").length > 0) {
              return of({ status: this.ValidationStatus.Valid } as EmailValidationInterface);
            }
            if (!p.params.email) {
              return of({
                email: p.params.email,
                status: "Valid",
                reason: "Required already triggered"
              } as EmailValidationInterface)
            }
            return this._srvc.validateEmail(p.params.email ?? "");
          }), take(1)))
        },
      }),
      onError: (error: unknown, ctx: ChildFieldContext<string>): TreeValidationResult => ({ kind: 'api-error', message: 'Server unreachable' }),
      onSuccess: (result: EmailValidationInterface, ctx: ChildFieldContext<string>): TreeValidationResult => (result.status !== this.ValidationStatus.Valid
        ? { kind: 'remoteValidateEmail', message: 'Email is not valid' }
        : null)
    });
    validateAsync(s.contact.phone, {
      params: (ctx: ChildFieldContext<string>): { phone: string, ctx: ChildFieldContext<string> } => {
        this.lastPhoneCall = ctx.value();
        return ({ phone: ctx.value(), ctx: ctx });
      },
      factory: (p: Signal<{ phone: string, ctx: ChildFieldContext<string> } | undefined>): ResourceRef<PhoneValidationInterface | undefined> => resource({
        params: () => {
          return p();
        },
        loader: (p) => {
          return lastValueFrom((timer(1000)).pipe(switchMap(() => {
            if ((p.params.phone ?? "") === "" || this.lastPhoneCall !== (p.params.phone ?? "") || p.params.ctx.state.errors().filter(x => x.kind !== "remoteValidatePhone").length > 0) {
              return of({ status: this.ValidationStatus.Valid } as PhoneValidationInterface);
            }
            if (!p.params.phone) {
              return of({
                phone: p.params.phone,
                status: "Valid",
                reason: "Required already triggered"
              } as PhoneValidationInterface)
            }
            return this._srvc.validatePhone(p.params.phone ?? "");
          }), take(1)))
        },
      }),
      onError: (error: unknown, ctx: ChildFieldContext<string>): TreeValidationResult => ({ kind: 'api-error', message: 'Server unreachable' }),
      onSuccess: (result: PhoneValidationInterface, ctx: ChildFieldContext<string>): TreeValidationResult => (result.status !== this.ValidationStatus.Valid
        ? { kind: 'remoteValidatePhone', message: result.reason ?? '' }
        : null)
    });
  });
  public origModel: AccountInterface | null = null;
  public phones = signal<Array<{ id: number; data: string }>>([]);
  public phonesForm = form(this.phones, (s) => {
    disabled(s, () => !this.isEditOn());
    applyEach(s, this._phonesSchema);
  });
  public emails = signal<Array<{ id: number; data: string }>>([]);
  public emailsForm = form(this.emails, (s) => {
    disabled(s, () => !this.isEditOn());
    applyEach(s, this._emailSchema)
  });
  public loyality: LoyalityDetailsInterface[] | null = null;
  public loyalityOrig: LoyalityDetailsInterface[] | null = null;
  public interestsLookup: Array<InterestInterface> | null = null;
  public isEditOn = signal<boolean>(false);
  public isCreateLoyalty = signal<boolean>(false);
  public selectedLoyaltyNumber = signal<string | null>(null);
  public selectedValamartistNumber = signal<string | null>(null);
  public offer = signal<SendOfferInterface | null>(null);
  public refreshAccount = new EventEmitter();
  public createNewClicked = new EventEmitter();
  public showPoints = signal<boolean>(false);
  public skillId: number | null = null;
  public isValidMail = false;
  public readonly noAuthenticationApiEndpoints: Array<string> = ["VrcApi/getToken", "VrcApi/getUserToken", "VrcApi/getApiToken"];
  // Only for merging duplicateAccount to account
  public mergeAccounts = signal<boolean>(false);
  public duplicateAccount: AccountEditInterface | null = null;
  public mergeSource: MergeSource = MergeSource.None;
  public parentWindow: WindowProxy | null = null;

  // Fields from url parameters
  public contactId = signal<number | null>(null);
  public email: string | null = null;
  public phone: string | null = null;
  public accountIds = signal<Array<string> | null>(null);
  public language: string | null = null;
  public firstName: string | null = null;
  public lastName: string | null = null;
  public contactType: 'email' | 'chat' | 'voice' = 'email';
  public sessionId: string | null = null;
  public timeOffset: string | null = null;
  public accountIsSelected = false;
  public niceNewContact = false;
  public readonly offerStatusError = "ERR";
  public readonly ValamartistProgram = "IFF";
  public readonly LoyaltyStatusActive = "Active";
  public readonly LoyaltyStatusPending = "Pending";

  public readonly channelTypes = {
    Email: 'Email',
    FAX: 'FAX',
    Chat: 'Chat',
    Voice: 'Voice',
    VoiceMail: 'VoiceMail',
    WorkItem: 'WorkItem',
    SMS: 'SMS',
    Social: 'Social',
    Digital: 'Digital',
  };

  public readonly commStatuses = {
    Open: 'Open',
    Active: 'Active',
    Closed: 'Closed',
    Parked: 'Parked',
    Inqueue: 'Inqueue',
    EmailTransfer: 'EmailTransfer',
    New: 'New',
    Escalated: 'Escalated',
    Resolved: 'Resolved',
    Pending: 'Pending',
    Unanswered: 'Unanswered',
  };

  public readonly ValidationStatus = {
    None: 'None',
    Valid: 'Valid',
    Invalid: 'Invalid',
    Unknown: 'Unknown',
    Forbidden: 'Forbidden'
  };

  public readonly offerStatuses = {
    CREATED: 'CREATED',
    SENT_EMAIL: 'EMAIL SENT',
    OPENED: 'OPENED',
    CANCEL: 'CANCEL',
    EXPIRED: 'EXPIRED',
    RESERVED: 'RESERVED',
  };

  public readonly reservationStatuses = {
    NEW: 'NEW',
    RESERVED: 'RESERVED',
    MODIFY: 'MODIFY',
    CANCEL: 'CANCEL',
    CHECKED_OUT: 'CHECKED OUT',
    CHECKED_IN: 'CHECKED IN',
    NO_SHOW: 'NO SHOW'
  };

  public readonly voucherTypes = {
    AMBASSADOR: 'AMBASSADOR',
    RECIPIENT: 'RECIPIENT',
    CONSUMER: 'CONSUMER',
  };

  public readonly supportedToOpenContentTypes: { [extension: string]: string } =
    {
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'bmp': 'image/bmp',
      'svg': 'image/svg+xml',
      'pdf': 'application/pdf'
    };

  public readonly genders: Array<string> = ['Male', 'Female', 'Other'];
  public readonly salutations: Array<string> = [
    'Gosp',
    'Gđa',
    'Gđica',
    'Dr',
    'Prof',
  ];
  public readonly dictionaryValues = ['Yes', 'No', 'Not specified'];
  public advertisingPermissions: { key: string, label: string }[] = [
    // { key: 'ad_email', label: 'Email' },
    // { key: 'ad_sms', label: 'Sms' },
    // { key: 'ad_viber', label: 'Viber' },
    // { key: 'ad_whatsApp', label: 'WhatsApp' },
    // { key: 'ad_push', label: 'Push' },
    // { key: 'ad_segmentation', label: 'Segmentation' }
  ];
  public products: { key: string, label: string }[] = [
    // { key: 'ad_valamar', label: 'Valamar' },
    // { key: 'ad_camping', label: 'Camping' },
    // { key: 'ad_bike', label: 'Bike' },
    // { key: 'ad_valfresco', label: 'Valfresco' }
  ];
  public readonly languages: Array<{ name: string; code: string | null }> = [
    { name: '', code: null },
    { name: 'cz', code: 'CZ' },
    { name: 'de', code: 'D' },
    { name: 'en', code: 'E' },
    { name: 'fr', code: 'F' },
    { name: 'hr', code: 'HR' },
    { name: 'it', code: 'I' },
    { name: 'pl', code: 'PL' },
    { name: 'sl', code: 'SL' },
    { name: 'sk', code: 'SK' },
    { name: 'Other', code: 'Other' },
  ];
  public readonly countries: Array<{ name: string; code: string | null }> = [
    { name: '', code: null },
    { name: 'Afghanistan', code: 'AF' },
    { name: 'Åland Islands', code: 'AX' },
    { name: 'Albania', code: 'AL' },
    { name: 'Algeria', code: 'DZ' },
    { name: 'American Samoa', code: 'AS' },
    { name: 'Andorra', code: 'AD' },
    { name: 'Angola', code: 'AO' },
    { name: 'Anguilla', code: 'AI' },
    { name: 'Antarctica', code: 'AQ' },
    { name: 'Antigua and Barbuda', code: 'AG' },
    { name: 'Argentina', code: 'AR' },
    { name: 'Armenia', code: 'AM' },
    { name: 'Aruba', code: 'AW' },
    { name: 'Australia', code: 'AU' },
    { name: 'Austria', code: 'AT' },
    { name: 'Azerbaijan', code: 'AZ' },
    { name: 'Bahamas', code: 'BS' },
    { name: 'Bahrain', code: 'BH' },
    { name: 'Bangladesh', code: 'BD' },
    { name: 'Barbados', code: 'BB' },
    { name: 'Belarus', code: 'BY' },
    { name: 'Belgium', code: 'BE' },
    { name: 'Belize', code: 'BZ' },
    { name: 'Benin', code: 'BJ' },
    { name: 'Bermuda', code: 'BM' },
    { name: 'Bhutan', code: 'BT' },
    { name: 'Bolivia', code: 'BO' },
    { name: 'Bosnia and Herzegovina', code: 'BA' },
    { name: 'Botswana', code: 'BW' },
    { name: 'Bouvet Island', code: 'BV' },
    { name: 'Brazil', code: 'BR' },
    { name: 'British Indian Ocean Territory', code: 'IO' },
    { name: 'British Antarctic Territory', code: 'BQ' },
    { name: 'Brunei Darussalam', code: 'BN' },
    { name: 'Bulgaria', code: 'BG' },
    { name: 'Burkina Faso', code: 'BF' },
    { name: 'Burundi', code: 'BI' },
    { name: 'Cambodia', code: 'KH' },
    { name: 'Cameroon', code: 'CM' },
    { name: 'Canada', code: 'CA' },
    { name: 'Cape Verde', code: 'CV' },
    { name: 'Cayman Islands', code: 'KY' },
    { name: 'Central African Republic', code: 'CF' },
    { name: 'Chad', code: 'TD' },
    { name: 'Chile', code: 'CL' },
    { name: 'China', code: 'CN' },
    { name: 'Christmas Island', code: 'CX' },
    { name: 'Cocos (Keeling) Islands', code: 'CC' },
    { name: 'Colombia', code: 'CO' },
    { name: 'Comoros', code: 'KM' },
    { name: 'Congo', code: 'CG' },
    { name: 'Congo, The Democratic Republic of the', code: 'CD' },
    { name: 'Cook Islands', code: 'CK' },
    { name: 'Costa Rica', code: 'CR' },
    { name: "Cote D'Ivoire", code: 'CI' },
    { name: 'Croatia', code: 'HR' },
    { name: 'Cuba', code: 'CU' },
    { name: 'Curacao', code: 'CW' },
    { name: 'Cyprus', code: 'CY' },
    { name: 'Czech Republic', code: 'CZ' },
    { name: 'Denmark', code: 'DK' },
    { name: 'Djibouti', code: 'DJ' },
    { name: 'Dominica', code: 'DM' },
    { name: 'Dominican Republic', code: 'DO' },
    { name: 'Ecuador', code: 'EC' },
    { name: 'Egypt', code: 'EG' },
    { name: 'El Salvador', code: 'SV' },
    { name: 'Equatorial Guinea', code: 'GQ' },
    { name: 'Eritrea', code: 'ER' },
    { name: 'Estonia', code: 'EE' },
    { name: 'Ethiopia', code: 'ET' },
    { name: 'Falkland Islands (Malvinas)', code: 'FK' },
    { name: 'Faroe Islands', code: 'FO' },
    { name: 'Fiji', code: 'FJ' },
    { name: 'Finland', code: 'FI' },
    { name: 'France', code: 'FR' },
    { name: 'French Guiana', code: 'GF' },
    { name: 'French Polynesia', code: 'PF' },
    { name: 'French Southern Territories', code: 'TF' },
    { name: 'Gabon', code: 'GA' },
    { name: 'Gambia', code: 'GM' },
    { name: 'Georgia', code: 'GE' },
    { name: 'Germany', code: 'DE' },
    { name: 'Ghana', code: 'GH' },
    { name: 'Gibraltar', code: 'GI' },
    { name: 'Greece', code: 'GR' },
    { name: 'Greenland', code: 'GL' },
    { name: 'Grenada', code: 'GD' },
    { name: 'Guadeloupe', code: 'GP' },
    { name: 'Guam', code: 'GU' },
    { name: 'Guatemala', code: 'GT' },
    { name: 'Guernsey', code: 'GG' },
    { name: 'Guinea', code: 'GN' },
    { name: 'Guinea-Bissau', code: 'GW' },
    { name: 'Guyana', code: 'GY' },
    { name: 'Haiti', code: 'HT' },
    { name: 'Heard Island and Mcdonald Islands', code: 'HM' },
    { name: 'Holy See (Vatican City State)', code: 'VA' },
    { name: 'Honduras', code: 'HN' },
    { name: 'Hong Kong', code: 'HK' },
    { name: 'Hungary', code: 'HU' },
    { name: 'Iceland', code: 'IS' },
    { name: 'India', code: 'IN' },
    { name: 'Indonesia', code: 'ID' },
    { name: 'Iran, Islamic Republic Of', code: 'IR' },
    { name: 'Iraq', code: 'IQ' },
    { name: 'Ireland', code: 'IE' },
    { name: 'Isle of Man', code: 'IM' },
    { name: 'Israel', code: 'IL' },
    { name: 'Italy', code: 'IT' },
    { name: 'Jamaica', code: 'JM' },
    { name: 'Japan', code: 'JP' },
    { name: 'Jersey', code: 'JE' },
    { name: 'Jordan', code: 'JO' },
    { name: 'Kazakhstan', code: 'KZ' },
    { name: 'Kenya', code: 'KE' },
    { name: 'Kiribati', code: 'KI' },
    { name: "Korea, Democratic People'S Republic of", code: 'KP' },
    { name: 'Korea, Republic of', code: 'KR' },
    { name: 'Kosovo', code: 'XK' },
    { name: 'Kuwait', code: 'KW' },
    { name: 'Kyrgyzstan', code: 'KG' },
    { name: "Lao People'S Democratic Republic", code: 'LA' },
    { name: 'Latvia', code: 'LV' },
    { name: 'Lebanon', code: 'LB' },
    { name: 'Lesotho', code: 'LS' },
    { name: 'Liberia', code: 'LR' },
    { name: 'Libyan Arab Jamahiriya', code: 'LY' },
    { name: 'Liechtenstein', code: 'LI' },
    { name: 'Lithuania', code: 'LT' },
    { name: 'Luxembourg', code: 'LU' },
    { name: 'Macao', code: 'MO' },
    { name: 'Macedonia, The Former Yugoslav Republic of', code: 'MK' },
    { name: 'Madagascar', code: 'MG' },
    { name: 'Malawi', code: 'MW' },
    { name: 'Malaysia', code: 'MY' },
    { name: 'Maldives', code: 'MV' },
    { name: 'Mali', code: 'ML' },
    { name: 'Malta', code: 'MT' },
    { name: 'Marshall Islands', code: 'MH' },
    { name: 'Martinique', code: 'MQ' },
    { name: 'Mauritania', code: 'MR' },
    { name: 'Mauritius', code: 'MU' },
    { name: 'Mayotte', code: 'YT' },
    { name: 'Mexico', code: 'MX' },
    { name: 'Micronesia, Federated States of', code: 'FM' },
    { name: 'Moldova, Republic of', code: 'MD' },
    { name: 'Monaco', code: 'MC' },
    { name: 'Mongolia', code: 'MN' },
    { name: 'Montenegro', code: 'ME' },
    { name: 'Montserrat', code: 'MS' },
    { name: 'Morocco', code: 'MA' },
    { name: 'Mozambique', code: 'MZ' },
    { name: 'Myanmar', code: 'MM' },
    { name: 'Namibia', code: 'NA' },
    { name: 'Nauru', code: 'NR' },
    { name: 'Nepal', code: 'NP' },
    { name: 'Netherlands', code: 'NL' },
    { name: 'Netherlands Antilles', code: 'AN' },
    { name: 'New Caledonia', code: 'NC' },
    { name: 'New Zealand', code: 'NZ' },
    { name: 'Nicaragua', code: 'NI' },
    { name: 'Niger', code: 'NE' },
    { name: 'Nigeria', code: 'NG' },
    { name: 'Niue', code: 'NU' },
    { name: 'Norfolk Island', code: 'NF' },
    { name: 'Northern Mariana Islands', code: 'MP' },
    { name: 'Norway', code: 'NO' },
    { name: 'Oman', code: 'OM' },
    { name: 'Pakistan', code: 'PK' },
    { name: 'Palau', code: 'PW' },
    { name: 'Palestinian Territory, Occupied', code: 'PS' },
    { name: 'Panama', code: 'PA' },
    { name: 'Papua New Guinea', code: 'PG' },
    { name: 'Paraguay', code: 'PY' },
    { name: 'Peru', code: 'PE' },
    { name: 'Philippines', code: 'PH' },
    { name: 'Pitcairn', code: 'PN' },
    { name: 'Poland', code: 'PL' },
    { name: 'Portugal', code: 'PT' },
    { name: 'Puerto Rico', code: 'PR' },
    { name: 'Qatar', code: 'QA' },
    { name: 'Reunion', code: 'RE' },
    { name: 'Romania', code: 'RO' },
    { name: 'Russian Federation', code: 'RU' },
    { name: 'RWANDA', code: 'RW' },
    { name: 'Saint Barthélemy', code: 'BL' },
    { name: 'Saint Helena', code: 'SH' },
    { name: 'Saint Kitts and Nevis', code: 'KN' },
    { name: 'Saint Lucia', code: 'LC' },
    { name: 'Saint Martin', code: 'MF' },
    { name: 'Saint Pierre and Miquelon', code: 'PM' },
    { name: 'Saint Vincent and the Grenadines', code: 'VC' },
    { name: 'Samoa', code: 'WS' },
    { name: 'San Marino', code: 'SM' },
    { name: 'Sao Tome and Principe', code: 'ST' },
    { name: 'Saudi Arabia', code: 'SA' },
    { name: 'Senegal', code: 'SN' },
    { name: 'Serbia', code: 'RS' },
    { name: 'Seychelles', code: 'SC' },
    { name: 'Sierra Leone', code: 'SL' },
    { name: 'Singapore', code: 'SG' },
    { name: 'Slovakia', code: 'SK' },
    { name: 'Slovenia', code: 'SI' },
    { name: 'Solomon Islands', code: 'SB' },
    { name: 'Somalia', code: 'SO' },
    { name: 'South Africa', code: 'ZA' },
    { name: 'South Georgia and the South Sandwich Islands', code: 'GS' },
    { name: 'Spain', code: 'ES' },
    { name: 'Sri Lanka', code: 'LK' },
    { name: 'Sudan', code: 'SD' },
    { name: 'Suriname', code: 'SR' },
    { name: 'Svalbard and Jan Mayen', code: 'SJ' },
    { name: 'Swaziland', code: 'SZ' },
    { name: 'Sweden', code: 'SE' },
    { name: 'Switzerland', code: 'CH' },
    { name: 'Syrian Arab Republic', code: 'SY' },
    { name: 'Taiwan, Province of China', code: 'TW' },
    { name: 'Tajikistan', code: 'TJ' },
    { name: 'Tanzania, United Republic of', code: 'TZ' },
    { name: 'Thailand', code: 'TH' },
    { name: 'Timor-Leste', code: 'TL' },
    { name: 'Togo', code: 'TG' },
    { name: 'Tokelau', code: 'TK' },
    { name: 'Tonga', code: 'TO' },
    { name: 'Trinidad and Tobago', code: 'TT' },
    { name: 'Tunisia', code: 'TN' },
    { name: 'Turkey', code: 'TR' },
    { name: 'Turkmenistan', code: 'TM' },
    { name: 'Turks and Caicos Islands', code: 'TC' },
    { name: 'Tuvalu', code: 'TV' },
    { name: 'Uganda', code: 'UG' },
    { name: 'Ukraine', code: 'UA' },
    { name: 'United Arab Emirates', code: 'AE' },
    { name: 'United Kingdom', code: 'GB' },
    { name: 'United States', code: 'US' },
    { name: 'United States Minor Outlying Islands', code: 'UM' },
    { name: 'Uruguay', code: 'UY' },
    { name: 'Uzbekistan', code: 'UZ' },
    { name: 'Vanuatu', code: 'VU' },
    { name: 'Venezuela', code: 'VE' },
    { name: 'Viet Nam', code: 'VN' },
    { name: 'Virgin Islands, British', code: 'VG' },
    { name: 'Virgin Islands, U.S.', code: 'VI' },
    { name: 'Wallis and Futuna', code: 'WF' },
    { name: 'Western Sahara', code: 'EH' },
    { name: 'Yemen', code: 'YE' },
    { name: 'Zambia', code: 'ZM' },
    { name: 'Zimbabwe', code: 'ZW' },
  ];
}
